import React from 'react'
import { Avatar, Popover } from 'antd'
import { EnvironmentOutlined, UserOutlined } from '@ant-design/icons'
import { Col, Container, Row } from 'reactstrap'
import { REQUEST_METHODS, ORDER_STATUS, ORDER_STATUS_CANCELLED } from '@app/common/constants/orders'
import { JOB_TYPES } from '@app/common/constants/jobTypes'
import { checkCanViewAppraiserNameOnOrder } from '@app/common/utils/helpers'
import Rephrase from '@app/common/utils/Rephrase'

// Badge type constants
const BADGE_TYPES = {
  WARNING: 'badge-warning',
  PRIMARY: 'badge-primary',
  DANGER: 'badge-danger',
  INFO: 'badge-info',
}

// Pre-acceptance status keys
const PRE_ACCEPTANCE_STATUSES = [
  ORDER_STATUS.FINDING_APPRAISERS,
  ORDER_STATUS.SCHEDULING_INSPECTION,
]

/**
 * Helper to render a badge with consistent styling
 * @param {string} badgeType - The badge class type (warning, primary, danger, info)
 * @param {React.ReactNode} content - The badge content
 * @returns {JSX.Element}
 */
const renderBadge = (badgeType, content) => (
  <small>
    <span className={`badge ${badgeType}`}>{content}</span>
  </small>
)

/**
 * Helper to render plain text in small tag
 * @param {string} text - The text to render
 * @param {string} id - Optional ID attribute
 * @returns {JSX.Element}
 */
const renderSmallText = (text, id = null) => <small id={id}>{text}</small>

/**
 * Generate a unique ID for the firm popover target
 * @returns {string}
 */
const generateFirmId = () => {
  return `firm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Renders the appraiser firm popover with details
 * @param {Object} accepted - The accepted firm data
 * @param {string} firmId - The unique ID for the popover target
 * @returns {JSX.Element}
 */
const AppraiserFirmPopover = ({ accepted, firmId }) => {
  const onTimePercentage =
    accepted.firm_past_orders && accepted.firm_past_on_time_percentage
      ? `${accepted.firm_past_on_time_percentage}%`
      : 'N/A'

  const popoverTitle = (
    <div className="popover-card-title">
      <Avatar size={64} icon={<UserOutlined />} />
      <br />
      <div className="appraiser-card-firm-name">{accepted.firm_name}</div>
      <br />
      {accepted.firm_location && (
        <div className="appraiser-card-icon">
          <EnvironmentOutlined />
          {accepted.firm_location}
        </div>
      )}
    </div>
  )

  const popoverContent = (
    <Container>
      <Row>
        <Col className="card-row">{accepted.firm_appraisers}</Col>
        <Col className="card-row">{accepted.firm_past_orders}</Col>
        <Col className="card-row card-row-last">{onTimePercentage}</Col>
      </Row>
      <Row>
        <Col className="card-labels">
          <Rephrase>Appraisers</Rephrase>
        </Col>
        <Col className="card-labels">Past Orders</Col>
        <Col className="card-labels">On Time</Col>
      </Row>
    </Container>
  )

  return (
    <Popover overlayClassName="order-appraiser-card" title={popoverTitle} content={popoverContent} target={firmId}>
      <small id={firmId}>{accepted.firm_name}</small>
    </Popover>
  )
}

/**
 * Checks if the order should display the "No Appraiser" badge
 * @param {Object} item - The order item
 * @param {Function} hasNoAppraiserLAR - Function to check for no appraiser LAR
 * @param {boolean} is_amc_lender - Whether this is an AMC lender
 * @returns {boolean}
 */
const shouldShowNoAppraiser = (item, hasNoAppraiserLAR, is_amc_lender) => {
  const hasLenderCanceled = item.lender_canceled === true
  const hasNoAppraiser = hasNoAppraiserLAR(item) && (item.job_type !== JOB_TYPES.AMC || is_amc_lender)

  return hasNoAppraiser || hasLenderCanceled
}

/**
 * Formats the appraiser column content based on order status, job type, and permissions.
 *
 * @param {Object} accepted - The accepted appraiser/firm data
 * @param {Object} item - The order item containing status, job_type, and other order data
 * @param {Object} options - Configuration options
 * @param {boolean} options.is_amc_lender - Whether the current lender is an AMC lender
 * @param {boolean} options.custom_panel - Whether the lender has a custom panel
 * @param {Function} options.hasNoAppraiserLAR - Function to check if order has no appraiser lender attention required
 * @returns {JSX.Element|null} The formatted appraiser column content
 */
export const formatAppraiserColumn = (accepted, item, options = {}) => {
  const { is_amc_lender = false, custom_panel = false, hasNoAppraiserLAR = () => false } = options

  const isPreAcceptance = PRE_ACCEPTANCE_STATUSES.includes(item.statusKey)
  const canViewAppraiserName = checkCanViewAppraiserNameOnOrder(item)
  const hasFirmName = accepted && accepted.firm_name

  // VA orders - show firm name or unassigned
  if (item.job_type === JOB_TYPES.VA && canViewAppraiserName) {
    return hasFirmName
      ? renderSmallText(accepted.firm_name, generateFirmId())
      : renderBadge(BADGE_TYPES.WARNING, 'Unassigned')
  }

  // Review orders - show Reggora Review badge
  if (item.job_type === JOB_TYPES.REVIEW) {
    return renderBadge(BADGE_TYPES.PRIMARY, 'Reggora Review')
  }

  // Waiting for payment status (non-cancelled)
  if (item.statusKey === ORDER_STATUS.WAITING_FOR_PAYMENT && item.status !== ORDER_STATUS_CANCELLED) {
    return renderBadge(BADGE_TYPES.WARNING, 'Waiting for Payment')
  }

  // AMC orders for non-AMC lenders without custom panel
  if (item.job_type === JOB_TYPES.AMC && !is_amc_lender && !custom_panel && canViewAppraiserName) {
    return renderBadge(BADGE_TYPES.PRIMARY, 'Reggora Network')
  }

  // Accepted order with firm name - show popover with details
  if (hasFirmName && canViewAppraiserName) {
    return <AppraiserFirmPopover accepted={accepted} firmId={generateFirmId()} />
  }

  // Pre-acceptance states - orders before appraiser acceptance
  if (isPreAcceptance) {
    // No appraiser LAR or lender canceled
    if (shouldShowNoAppraiser(item, hasNoAppraiserLAR, is_amc_lender)) {
      return renderBadge(BADGE_TYPES.DANGER, <Rephrase>No Appraiser</Rephrase>)
    }

    // Broadcast order request method
    if (item.order_request_method === REQUEST_METHODS.BROADCAST) {
      return renderBadge(BADGE_TYPES.PRIMARY, 'Broadcast')
    }

    // Cascade order request method
    if (item.order_request_method === REQUEST_METHODS.CASCADE) {
      return renderBadge(BADGE_TYPES.PRIMARY, 'Cascade')
    }

    // Requested appraiser pending acceptance
    if (item.requested_appraiser && canViewAppraiserName) {
      return (
        <small>
          {item.requested_appraiser.firm_name}{' '}
          <span className="d-block">
            <span className={`badge ${BADGE_TYPES.INFO}`}>Pending</span>
          </span>
        </small>
      )
    }

    // Automatically assigned job type
    if (item.job_type === JOB_TYPES.AUTOMATIC) {
      return renderBadge(BADGE_TYPES.PRIMARY, <Rephrase>Calculating Appraiser</Rephrase>)
    }

    // AMC job type
    if (item.job_type === JOB_TYPES.AMC) {
      return renderBadge(BADGE_TYPES.PRIMARY, <Rephrase>Assigning Appraiser</Rephrase>)
    }
  }

  // Default: return null if no conditions match
  return null
}
